import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useLayoutEffect,
} from 'react'
import { useParams, Link } from 'react-router'
import { Icon } from '@/design-system/atoms/Icon'
import { Spinner } from '@/design-system/atoms/Spinner'
import { useAuth } from '../../context/auth'
import { useAgents } from '../../context/agents'
import { useSidebar } from '../../context/sidebar'
import chatApi from '../../services/chatService'
import { sharingApi } from '../../services/sharingService'
import metadataService from '../../services/metadataService'
import { PageTitleService } from '../../hooks/usePageTitle'
import {
  ChatMessage,
  ChatInput,
  SessionHistorySidebar,
  SnippetSelector,
  SnippetFormModal,
  ProcessingIndicator,
  EmptyStateWelcome,
  ShareSessionModal,
} from '../../features/chat'
import {
  PROCESSING_STATUS,
  type ProcessingStatus,
} from '../../constants/processingMessages'
import { MetadataSidebar } from '../../features/metadata'
import { useSessionStartTimes } from '../../features/chat/hooks/useSessionStartTimes'
import { useDateFormatter } from '../../hooks/useDateFormatter'
import { usePersistedState } from '../../hooks/usePersistedState'
import type { AgentSnippet } from '../../types/snippets'
import type { MetadataFieldSchema } from '../../config/metadataSchemas'
import { useSearchParams } from 'react-router'
import { describeApiError } from '@/lib/apiError'

interface MessageSender {
  id: number
  name: string
  username: string
}

interface SystemMessageMetadata {
  action?: string
  actorId?: number
  actorName?: string
  targetName?: string
  targetType?: 'user' | 'group'
}

interface ChatMessageData {
  id: string
  type: 'user' | 'agent' | 'system'
  content: string
  timestamp: string
  sender?: MessageSender
  metadata?: SystemMessageMetadata
}

import type { ShareAccessLevel, UserMinimal } from '../../types/agents'

interface ChatSession {
  id: string
  title: string
  lastMessage?: string
  timestamp: string
  agentType: string
  messageCount: number
  status?: string
  customer?: string
  serviceOrder?: string
  plate?: string
  vehicleModel?: string
  // Sharing fields
  isShared?: boolean
  isOwner?: boolean
  myAccessLevel?: ShareAccessLevel | 'owner' | null
  sharedBy?: UserMinimal | null
  participantsCount?: number
  // Unread tracking for shared sessions
  hasUnread?: boolean
}

const groupSessionsByDate = (
  sessions: ChatSession[],
  formatDateSection: (date: string) => string
) => {
  const grouped = sessions.reduce(
    (groups, session) => {
      const dateKey = new Date(session.timestamp).toDateString()
      if (!groups[dateKey]) {
        groups[dateKey] = []
      }
      groups[dateKey].push(session)
      return groups
    },
    {} as Record<string, ChatSession[]>
  )

  return Object.keys(grouped)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
    .map(dateKey => ({
      date: dateKey,
      displayDate: formatDateSection(dateKey),
      sessions: grouped[dateKey].sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      ),
    }))
}

export default function AgentChat() {
  const { type } = useParams<{ type: string }>()
  const { user, permissions, features } = useAuth()
  const { agents, isLoading: agentsLoading } = useAgents()

  // Check if chat sharing feature is enabled
  const chatSharingEnabled = features?.chat_sharing_enabled ?? false
  const { collapseSidebar } = useSidebar()
  const { formatDateSection, formatTime } = useDateFormatter()

  // Force scroll to top immediately when mounting to prevent layout displacement
  useLayoutEffect(() => {
    window.scrollTo(0, 0)
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
  }, [])

  const [messages, setMessages] = useState<ChatMessageData[]>([])
  const [inputValue, setInputValue] = useState('')
  const [loadingSessions, setLoadingSessions] = useState<Set<string>>(new Set())
  // Processing state - unified status and start time for timer
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>(
    PROCESSING_STATUS.IDLE
  )
  const [processingStartTime, setProcessingStartTime] = useState<number | null>(
    null
  )
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [currentSessionTitle, setCurrentSessionTitle] = useState<string>('')

  // Track active polling operations to prevent state mixing during navigation
  const activePollingSession = useRef<string | null>(null)

  // Store processing start time per session (preserves timer when switching sessions)
  // Extracted to hook — persists via localStorage.
  const {
    ref: sessionStartTimes,
    set: setSessionStartTime,
    remove: deleteSessionStartTime,
  } = useSessionStartTimes()
  const [isHistorySidebarOpen, setIsHistorySidebarOpen] = useState(false)
  const [isRightSidebarExpanded, setIsRightSidebarExpanded] =
    usePersistedState<boolean>('right-sidebar-expanded', true)
  const toggleRightSidebar = useCallback(
    () => setIsRightSidebarExpanded(prev => !prev),
    [setIsRightSidebarExpanded]
  )
  const [sidebarMode, setSidebarMode] = useState<'history' | 'metadata'>(
    'history'
  )
  // Metadata state - 100% agnostic of backend schema
  const [metadata, setMetadata] = useState<Record<string, unknown>>({})
  const [metadataSchema, setMetadataSchema] = useState<
    MetadataFieldSchema[] | null
  >(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // AbortController ref to cancel ongoing polling
  const pollingAbortController = useRef<AbortController | null>(null)

  // Track which session is currently being loaded (for race condition detection)
  const currentlyLoadingSessionRef = useRef<string | null>(null)

  // Unique ID for each loadSession call (prevents race conditions with same session)
  const loadSessionCallIdRef = useRef<number>(0)

  // Track current session ID synchronously (for immediate state cleanup)
  const currentSessionIdRef = useRef<string | null>(null)

  // Track when loading a DIFFERENT session (prevents showing old messages during fetch)
  const [isLoadingDifferentSession, setIsLoadingDifferentSession] =
    useState(false)

  // Snippets state
  const [showSnippets, setShowSnippets] = useState(false)
  const [selectedSnippet, setSelectedSnippet] = useState<AgentSnippet | null>(
    null
  )
  const [showSnippetForm, setShowSnippetForm] = useState(false)

  // Sharing state
  const [shareModalSession, setShareModalSession] =
    useState<ChatSession | null>(null)

  // Unread sessions tracking (for shared sessions)
  // Note: unreadSessionIds is set but only used to update session.hasUnread
  const [, setUnreadSessionIds] = useState<Set<string>>(new Set())

  // URL é a single source of truth da sessão ativa. `currentSessionId` é
  // derivado; para trocar de sessão, use `navigateToSession` que atualiza a
  // URL e dispara o effect de carregamento. Assim qualquer ponto do app
  // (sidebar, kanban, scheduler, deeplink externo, refresh) converge.
  const [searchParams, setSearchParams] = useSearchParams()
  const currentSessionId = searchParams.get('sessionId')
  const navigateToSession = useCallback(
    (sessionId: string | null) => {
      // Usa setter funcional para não depender de `searchParams` (que muda
      // a cada update de URL e invalidaria dependents do navigateToSession).
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          if (sessionId) next.set('sessionId', sessionId)
          else next.delete('sessionId')
          return next
        },
        { replace: false }
      )
    },
    [setSearchParams]
  )

  // Derived state: check if current session is loading
  const isCurrentSessionLoading = useMemo(() => {
    // For new chats (no sessionId), use a special key 'new'
    const sessionKey = currentSessionId || 'new'
    return loadingSessions.has(sessionKey)
  }, [loadingSessions, currentSessionId])

  // Get current session's access level for permission checks
  const currentSession = useMemo(() => {
    return sessions.find(s => s.id === currentSessionId) || null
  }, [sessions, currentSessionId])

  // Check if user can send messages (owner, participate, or new chat)
  const canSendMessages = useMemo(() => {
    // New chat - always can send
    if (!currentSessionId) return true
    // Owner - always can send
    if (currentSession?.isOwner) return true
    // Participate access - can send
    if (currentSession?.myAccessLevel === 'participate') return true
    if (currentSession?.myAccessLevel === 'owner') return true
    // View access - cannot send
    return false
  }, [currentSessionId, currentSession])

  // Snippet handlers
  const handleSnippetsClick = () => {
    setShowSnippets(true)
  }

  const handleSnippetSelect = (snippet: AgentSnippet) => {
    setShowSnippets(false)

    if (snippet.requires_input) {
      setSelectedSnippet(snippet)
      setShowSnippetForm(true)
    } else {
      // Snippet without input - auto-send immediately with tool bypass
      sendMessage(snippet.template, snippet.tool)
    }
  }

  const handleSnippetFormSubmit = (
    message: string,
    tool?: string,
    toolParams?: Record<string, unknown>
  ) => {
    setShowSnippetForm(false)
    setSelectedSnippet(null)

    // Auto-send message with tool bypass for routing optimization
    sendMessage(message, tool, toolParams)
  }

  // Sharing handlers
  const handleShareSession = (session: ChatSession) => {
    setShareModalSession(session)
  }

  const handleSharesUpdated = () => {
    // Reload sessions to update sharing state
    loadSessionsData()
  }

  // Find agent from context - no API call needed!
  const agent = useMemo(() => {
    return agents.find(a => a.type === type) || null
  }, [agents, type])

  // Set page title when agent changes
  useEffect(() => {
    if (agent) {
      PageTitleService.setTitle(agent.name)
    }
  }, [agent])

  // Sync currentSessionId state with ref (for synchronous access)
  useEffect(() => {
    currentSessionIdRef.current = currentSessionId
  }, [currentSessionId])

  // Reset state when agent type changes.
  // NÃO limpamos `sessionId` da URL aqui: a URL é a single source of truth.
  // Quando o usuário troca de agente, a própria navegação (`navigate
  // ('/agents/X/chat')`) já chega sem sessionId. Mantê-lo intocado aqui
  // preserva deeplinks de bootstrap (`?sessionId=...` no cold mount).
  useEffect(() => {
    setMessages([])
    setCurrentSessionTitle('')
    setInputValue('')
    setSidebarMode('history') // Reset to history on agent change

    // Reset scroll positions
    const messagesArea = document.querySelector(
      '[data-testid="chat-messages-area"]'
    )
    if (messagesArea) {
      messagesArea.scrollTop = 0
    }

    // Reset sidebar scroll
    const sidebarContent = document.querySelector('.custom-scrollbar')
    if (sidebarContent) {
      sidebarContent.scrollTop = 0
    }
  }, [type])

  // Sidebar always defaults to history — user can toggle manually to metadata
  // (auto-switch removed: history is more useful as default view)

  // Collapse sidebar when entering chat
  useEffect(() => {
    collapseSidebar()
  }, [collapseSidebar])

  // Track if sessions are already loading to prevent duplicate calls
  const loadingSessionsRef = useRef(false)

  // Load sessions data - memoized callback
  const loadSessionsData = useCallback(async () => {
    if (!type || loadingSessionsRef.current) return

    // Wait for agents to load before checking permissions
    if (agentsLoading) return

    // Check permission before making API call
    const hasAccess =
      permissions?.allowedAgents?.includes(type) || permissions?.canManageUsers
    if (!hasAccess) return

    loadingSessionsRef.current = true
    try {
      const list = await chatApi.listSessions(type)

      // Fetch unread count for shared sessions (if sharing is enabled)
      let unreadIds = new Set<string>()
      if (chatSharingEnabled) {
        try {
          const unreadData = await sharingApi.getUnreadCount()
          unreadIds = new Set(unreadData.sessions.map(s => s.sessionId))
          setUnreadSessionIds(unreadIds)
        } catch {
          // Silent fail - unread feature is optional
        }
      }

      // Merge with existing sessions to preserve optimistic updates
      // If local session has higher messageCount (due to optimistic update), keep local value
      setSessions(prev => {
        if (prev.length === 0) {
          // Add hasUnread to sessions
          return list.map(s => ({ ...s, hasUnread: unreadIds.has(s.id) }))
        }
        return list.map(serverSession => {
          const localSession = prev.find(s => s.id === serverSession.id)
          const hasUnread = unreadIds.has(serverSession.id)
          if (
            localSession &&
            localSession.messageCount > serverSession.messageCount
          ) {
            // Keep optimistic messageCount and timestamp
            return {
              ...serverSession,
              messageCount: localSession.messageCount,
              timestamp: localSession.timestamp,
              hasUnread,
            }
          }
          return { ...serverSession, hasUnread }
        })
      })
    } catch {
      setSessions([])
    } finally {
      loadingSessionsRef.current = false
    }
  }, [
    type,
    agentsLoading,
    permissions?.allowedAgents,
    permissions?.canManageUsers,
    chatSharingEnabled,
  ])

  useEffect(() => {
    loadSessionsData()
  }, [loadSessionsData])

  // Memoize grouped sessions to prevent unnecessary recalculations
  const groupedSessions = useMemo(
    () => groupSessionsByDate(sessions, formatDateSection),
    [sessions, formatDateSection]
  )

  // Update page title when session changes - memoized to prevent unnecessary updates
  useEffect(() => {
    const agentName = agent?.name
    if (agentName && currentSessionTitle) {
      PageTitleService.setTitle(currentSessionTitle)
    } else if (agentName) {
      PageTitleService.setTitle(agentName)
    }
  }, [agent?.name, currentSessionTitle])

  // Scroll to bottom when messages change - with performance optimization
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 50) // Small delay to ensure DOM is updated

    return () => clearTimeout(timeoutId)
  }, [messages.length]) // Only trigger on message count change, not content change

  // Auto-update session title when metadata (chassi/OS) changes
  useEffect(() => {
    if (!currentSessionId || type !== 'warranties') return

    const chassi = metadata.chassis_suffix as string
    const os = metadata.service_order as string

    // Only update if we have at least one identifier
    if (!chassi && !os) return

    // Generate new title based on metadata
    let newTitle = ''
    if (chassi && os) {
      newTitle = `Chassi: ${chassi} | OS: ${os}`
    } else if (chassi) {
      newTitle = `Chassi: ${chassi}`
    } else if (os) {
      newTitle = `OS: ${os}`
    }

    // Update session title if changed
    if (newTitle && newTitle !== currentSessionTitle) {
      setCurrentSessionTitle(newTitle)

      // Update in sessions list
      setSessions(prev =>
        prev.map(s =>
          s.id === currentSessionId ? { ...s, title: newTitle } : s
        )
      )

      // Update on backend
      updateSessionTitle(currentSessionId, newTitle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadata.chassis_suffix, metadata.service_order, currentSessionId, type])

  // Memoize permission check to prevent unnecessary recalculations
  const hasPermission = useMemo(
    () =>
      permissions?.allowedAgents?.includes(type || '') ||
      permissions?.canManageUsers,
    [permissions?.allowedAgents, permissions?.canManageUsers, type]
  )

  // Gerar título automático baseado nos metadados ou primeira mensagem
  const generateAutoTitle = (firstMessage: string): string => {
    // Prioridade 1: Usar metadados (chassi e/ou OS) se disponíveis
    if (type === 'warranties' && metadata) {
      const chassi = metadata.chassis_suffix as string
      const os = metadata.service_order as string

      if (chassi && os) {
        return `Chassi: ${chassi} | OS: ${os}`
      } else if (chassi) {
        return `Chassi: ${chassi}`
      } else if (os) {
        return `OS: ${os}`
      }
    }

    // Prioridade 2: Usar a primeira mensagem do usuário
    const maxLength = 50
    let title = firstMessage.trim()

    // Remover quebras de linha e espaços extras
    title = title.replace(/\s+/g, ' ')

    // Truncar se muito longo
    if (title.length > maxLength) {
      title = title.substring(0, maxLength) + '...'
    }

    // Prioridade 3: Usar data/hora se não houver nada
    if (!title) {
      const now = new Date()
      return `Conversa ${now.toLocaleDateString('pt-BR')} ${now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
    }

    return title
  }

  const startNewChat = () => {
    // Abort any ongoing polling when starting new chat
    if (pollingAbortController.current) {
      pollingAbortController.current.abort()
      pollingAbortController.current = null
    }

    setMessages([])
    navigateToSession(null)
    setCurrentSessionTitle('')
    setMetadata({})
  }

  const handleCancelProcessing = useCallback(() => {
    // Abort polling
    if (pollingAbortController.current) {
      pollingAbortController.current.abort()
      pollingAbortController.current = null
    }

    // Clear loading state for current session
    const sessionKey = currentSessionId || 'new'
    setLoadingSessions(prev => {
      const next = new Set(prev)
      next.delete(sessionKey)
      return next
    })

    // Reset processing status
    setProcessingStatus(PROCESSING_STATUS.IDLE)
    setProcessingStartTime(null)

    // Add cancellation message to chat
    const cancelMsg: ChatMessageData = {
      id: `cancel-${Date.now()}`,
      type: 'system',
      content: 'Processamento cancelado pelo usuário.',
      timestamp: new Date().toISOString(),
    }
    setMessages(prev => [...prev, cancelMsg])
  }, [currentSessionId])

  const updateSessionTitle = async (sessionId: string, newTitle: string) => {
    // Armazenar título original para possível reversão
    const originalTitle =
      sessions.find(s => s.id === sessionId)?.title || newTitle

    // Otimista no UI
    setSessions(prev =>
      prev.map(session =>
        session.id === sessionId ? { ...session, title: newTitle } : session
      )
    )
    if (currentSessionId === sessionId) {
      setCurrentSessionTitle(newTitle)
    }

    // Persistir no backend
    try {
      await chatApi.updateSessionTitle(sessionId, newTitle)
    } catch {
      // Reverter mudança otimista em caso de erro
      setSessions(prev =>
        prev.map(session =>
          session.id === sessionId
            ? { ...session, title: originalTitle }
            : session
        )
      )
      if (currentSessionId === sessionId) {
        setCurrentSessionTitle(originalTitle)
      }
    }
  }

  const updateSessionStatus = async (sessionId: string, newStatus: string) => {
    const originalStatus =
      sessions.find(s => s.id === sessionId)?.status || 'in_progress'

    // otimista
    setSessions(prev =>
      prev.map(session =>
        session.id === sessionId ? { ...session, status: newStatus } : session
      )
    )

    try {
      await chatApi.updateSessionStatus(sessionId, newStatus)
      if (currentSessionId === sessionId) {
        // keep local current if needed in future
      }
    } catch {
      // reverte
      setSessions(prev =>
        prev.map(session =>
          session.id === sessionId
            ? { ...session, status: originalStatus }
            : session
        )
      )
    }
  }

  const updateMetadata = async (field: string, value: unknown) => {
    if (!currentSessionId) return

    // Auto-fill chassis_suffix when chassis_number is filled
    if (
      field === 'chassis_number' &&
      typeof value === 'string' &&
      value.length >= 7
    ) {
      const chassisSuffix = value.slice(-7)

      // Only auto-fill if chassis_suffix is empty
      if (!metadata.chassis_suffix) {
        // Update both fields
        await metadataService.updateField(currentSessionId, {
          field: 'chassis_suffix',
          value: chassisSuffix,
        })
      }
    }

    const updatedSession = await metadataService.updateField(currentSessionId, {
      field,
      value,
    })

    // Update metadata from server response
    const validatedMetadata = updatedSession.validated_metadata || {}

    setMetadata(validatedMetadata as Record<string, unknown>)
  }

  const loadSession = useCallback(
    async (sessionId: string) => {
      // Generate unique ID for this call (prevents race conditions)
      const callId = ++loadSessionCallIdRef.current

      // Store previous session ID to detect if we're switching sessions
      const previousSessionId = currentSessionIdRef.current
      const isSwitchingSession =
        previousSessionId !== null && previousSessionId !== sessionId

      // eslint-disable-next-line no-console
      console.log('[loadSession] START', {
        callId,
        sessionId,
        previousSessionId,
        isSwitchingSession,
        savedStartTime: sessionStartTimes.current.get(sessionId),
        activePolling: activePollingSession.current,
      })

      // Set ref to track which session we're loading (synchronous)
      currentlyLoadingSessionRef.current = sessionId

      // Show loading state when switching sessions
      if (isSwitchingSession) {
        setIsLoadingDifferentSession(true)
      }

      // ALWAYS abort previous polling when switching sessions
      // This prevents race conditions and state mixing
      if (pollingAbortController.current) {
        pollingAbortController.current.abort()
      }

      // Create new AbortController for this session
      const abortController = new AbortController()
      pollingAbortController.current = abortController

      // When switching sessions, clear states from previous session
      if (isSwitchingSession) {
        setMetadata({})
        // DON'T clear processingStatus/processingStartTime here
        // We'll restore them from sessionStartTimes if there's an active job
      }

      // URL já foi atualizada pelo caller (navigateToSession); aqui só
      // sincronizamos a ref síncrona e carregamos o title. Se a sessão não
      // está em `sessions` (ex: criada pelo scheduler fora da listagem),
      // mantém um placeholder que será corrigido quando a lista chegar.
      currentSessionIdRef.current = sessionId
      const session = sessions.find(s => s.id === sessionId)
      setCurrentSessionTitle(session?.title ?? 'Carregando...')

      // Mark session as read if it's a shared session (not owner)
      if (
        chatSharingEnabled &&
        session &&
        !session.isOwner &&
        session.hasUnread
      ) {
        sharingApi.markSessionRead(sessionId).catch(() => {
          // Silent fail - marking as read is not critical
        })
        // Update local state to remove unread indicator
        setSessions(prev =>
          prev.map(s => (s.id === sessionId ? { ...s, hasUnread: false } : s))
        )
        setUnreadSessionIds(prev => {
          const next = new Set(prev)
          next.delete(sessionId)
          return next
        })
      }

      const sessionKey = sessionId
      setLoadingSessions(prev => new Set(prev).add(sessionKey))

      // Helper to check if this call is still the current one
      const isStaleCall = () => loadSessionCallIdRef.current !== callId

      try {
        // Step 1: Load history FIRST so we can show messages immediately
        const historyData = await chatApi.getHistory(sessionId)

        // Check if this call is stale (newer call started)
        if (isStaleCall()) {
          // eslint-disable-next-line no-console
          console.log('[loadSession] Stale call after history, returning', {
            callId,
            currentCallId: loadSessionCallIdRef.current,
          })
          return
        }

        // Step 2: Show messages immediately
        setMessages(historyData.messages)
        setIsLoadingDifferentSession(false)

        // Step 3: Check for ongoing jobs and resume polling
        // Get saved startTime for this session (if any)
        const savedStartTime = sessionStartTimes.current.get(sessionId)

        const pollingResult = await chatApi.resumePollingIfNeeded({
          sessionId,
          onStatusChange: (status, backendStartTime) => {
            // Only update if this call is still current
            if (isStaleCall()) {
              // eslint-disable-next-line no-console
              console.log('[loadSession] onStatusChange SKIPPED (stale call)', {
                callId,
                currentCallId: loadSessionCallIdRef.current,
              })
              return
            }

            // eslint-disable-next-line no-console
            console.log('[loadSession] onStatusChange', {
              callId,
              sessionId,
              status,
              backendStartTime,
              savedStartTime,
            })

            setProcessingStatus(status)
            activePollingSession.current = sessionId

            // Use backend startTime if provided, otherwise use saved time
            const startTime = backendStartTime || savedStartTime || Date.now()
            setProcessingStartTime(startTime)

            // Save startTime for this session (for when user returns)
            setSessionStartTime(sessionId, startTime)
          },
          abortSignal: abortController.signal,
        })

        // eslint-disable-next-line no-console
        console.log('[loadSession] resumePollingIfNeeded returned', {
          callId,
          currentCallId: loadSessionCallIdRef.current,
          sessionId,
          pollingResult: !!pollingResult,
        })

        // Check if this call is stale (newer call started during polling)
        if (isStaleCall()) {
          // eslint-disable-next-line no-console
          console.log(
            '[loadSession] Stale call after polling, returning early',
            { callId }
          )
          return
        }

        // Handle polling result
        if (pollingResult) {
          // Job completed successfully
          const aiMsg: ChatMessageData = {
            id: `ai-${Date.now()}`,
            type: 'agent',
            content: pollingResult.response,
            timestamp: new Date().toISOString(),
          }
          setMessages(prev => [...prev, aiMsg])

          // Update metadata from poll response (already flat structure, most recent)
          if (type === 'warranties') {
            if (pollingResult.agentMetadata) {
              setMetadata(pollingResult.agentMetadata)
            }
            // Always load schema from history (schema doesn't come from polling)
            if (historyData.metadataSchema) {
              setMetadataSchema(historyData.metadataSchema.fields || [])
            }
            // Progresso calculado localmente a partir dos campos
          }

          if (pollingResult.title && session) {
            setSessions(prev =>
              prev.map(s =>
                s.id === sessionId ? { ...s, title: pollingResult.title! } : s
              )
            )
            setCurrentSessionTitle(pollingResult.title)
          }

          // Job completed - clear processing state and saved startTime
          deleteSessionStartTime(sessionId)
          activePollingSession.current = null
          setProcessingStatus(PROCESSING_STATUS.IDLE)
          setProcessingStartTime(null)
        } else {
          // eslint-disable-next-line no-console
          console.log('[loadSession] No active job, clearing state', {
            sessionId,
          })

          // No active polling - load metadata and schema from history response
          if (type === 'warranties') {
            setMetadata(historyData.agentMetadata)
            if (historyData.metadataSchema) {
              setMetadataSchema(historyData.metadataSchema.fields || [])
            }
          }

          // No job running - clear processing state
          activePollingSession.current = null
          setProcessingStatus(PROCESSING_STATUS.IDLE)
          setProcessingStartTime(null)
        }
      } catch (error) {
        // Check if error is from AbortController (expected when switching sessions)
        if (error instanceof Error && error.name === 'AbortError') {
          // Don't update UI - let the new loadSession() call handle it
          // NOTE: finally block will still execute even with this return
          return
        }

        // Unexpected error - log details and show error message instead of empty screen
        console.error('[loadSession] Failed to load session:', sessionId, error) // eslint-disable-line no-console

        // Show error message instead of empty screen
        const errorMsg: ChatMessageData = {
          id: `error-${Date.now()}`,
          type: 'agent',
          content: `Erro ao carregar a conversa: ${describeApiError(error)}`,
          timestamp: new Date().toISOString(),
        }
        setMessages([errorMsg])

        // Clear metadata/progress on error
        setMetadata({})
      } finally {
        // Only clean up if this call is still the current one
        // If a newer call started, don't touch states (newer call will handle it)
        const isCurrentCall = loadSessionCallIdRef.current === callId

        // eslint-disable-next-line no-console
        console.log('[loadSession] finally', {
          callId,
          isCurrentCall,
          currentCallId: loadSessionCallIdRef.current,
        })

        if (isCurrentCall) {
          // Always clear the "different session" loading flag
          setIsLoadingDifferentSession(false)

          // Only clear loadingSessions if no active polling for this session
          const hasActivePolling = activePollingSession.current === sessionId
          if (!hasActivePolling) {
            setLoadingSessions(prev => {
              const next = new Set(prev)
              next.delete(sessionKey)
              return next
            })
          }
        }
        // If not current session, don't touch any states - let the new session handle cleanup
      }
    },
    [
      sessions,
      type,
      sessionStartTimes,
      setSessionStartTime,
      deleteSessionStartTime,
      chatSharingEnabled,
    ]
  )

  const sendMessage = async (
    messageOverride?: string,
    tool?: string,
    toolParams?: Record<string, unknown>
  ) => {
    const message = messageOverride || inputValue
    const text = typeof message === 'string' ? message.trim() : ''
    if (!text || !type) return

    // Check if current session is already loading; if ficou preso em loading, liberamos o envio
    const sessionKey = currentSessionId || 'new'
    if (loadingSessions.has(sessionKey)) {
      // fallback para liberar novo envio mesmo se algum loading antigo não limpou
      setLoadingSessions(prev => {
        const next = new Set(prev)
        next.delete(sessionKey)
        return next
      })
    }

    setInputValue('')

    const now = new Date().toISOString()
    const userMsg: ChatMessageData = {
      id: `local-${Date.now()}`,
      type: 'user',
      content: text,
      timestamp: now,
    }
    setMessages(prev => [...prev, userMsg])

    // Immediately increment message count for existing sessions (user message)
    if (currentSessionId) {
      // eslint-disable-next-line no-console
      console.log('[sendMessage] Incrementing messageCount for user message', {
        currentSessionId,
      })
      setSessions(prev => {
        const updated = prev.map(s => {
          if (s.id === currentSessionId) {
            // eslint-disable-next-line no-console
            console.log('[sendMessage] Found session, incrementing', {
              id: s.id,
              oldCount: s.messageCount,
              newCount: s.messageCount + 1,
            })
            return { ...s, messageCount: s.messageCount + 1, timestamp: now }
          }
          return s
        })
        return updated
      })
    }

    // Mark current session as loading
    setLoadingSessions(prev => new Set(prev).add(sessionKey))
    // Reset processing state for new message
    setProcessingStatus(PROCESSING_STATUS.IDLE)
    setProcessingStartTime(null)

    // Abort any existing polling before starting new one
    if (pollingAbortController.current) {
      pollingAbortController.current.abort()
    }

    // Create new AbortController for this message
    const abortController = new AbortController()
    pollingAbortController.current = abortController

    // Track which session this polling operation belongs to
    // This prevents state mixing when user navigates during polling
    const pollingTargetSession = currentSessionId || 'new-session'
    activePollingSession.current = pollingTargetSession

    try {
      // Use polling-based endpoint for long-running operations
      const { sessionId, response, title, agentMetadata } =
        await chatApi.sendMessage({
          agentType: type,
          message: text,
          sessionId: currentSessionId,
          tool,
          toolParams,
          onStatusChange: (status, startTime) => {
            // Ignora updates se usuário mudou de sessão durante polling
            const currentViewing = currentSessionIdRef.current
            const targetSession =
              activePollingSession.current || pollingTargetSession
            if (currentViewing !== null && currentViewing !== targetSession) {
              return
            }

            setProcessingStatus(status)
            if (startTime) {
              setProcessingStartTime(startTime)
              // Save startTime for session (for when user navigates away and back)
              const targetSessionId =
                currentSessionId || activePollingSession.current
              if (targetSessionId) {
                setSessionStartTime(targetSessionId, startTime)
              }
            }
          },
          onSessionCreated: (newSessionId: string, sessionTitle?: string) => {
            // Add session to list IMMEDIATELY (appears in history without F5)
            // ✅ CRITICAL: DON'T set as current session here - causes re-render that clears loading states
            if (!currentSessionId) {
              const autoTitle = sessionTitle || generateAutoTitle(text)
              const newSession: ChatSession = {
                id: newSessionId,
                title: autoTitle,
                agentType: type,
                messageCount: 1, // Only user message exists at this point
                timestamp: new Date().toISOString(),
                status: 'in_progress',
                isOwner: true, // User who creates the session is always the owner
              }
              setSessions(prev => [newSession, ...prev])
              // Update tracking to use actual session ID
              activePollingSession.current = newSessionId
              // ✅ REMOVED: setCurrentSessionId(newSessionId) - moved to after polling completes
              // ✅ REMOVED: setCurrentSessionTitle(autoTitle) - moved to after polling completes
            }
          },
          onIntermediateResponse: (
            intermediateResponse,
            intermediateMetadata
          ) => {
            // Composite tool step completed — add response to chat immediately
            const aiMsg: ChatMessageData = {
              id: `ai-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
              type: 'agent',
              content: intermediateResponse,
              timestamp: new Date().toISOString(),
            }
            setMessages(prev => [...prev, aiMsg])

            // Update session message count
            const targetId = currentSessionId || activePollingSession.current
            if (targetId) {
              setSessions(prev =>
                prev.map(s =>
                  s.id === targetId
                    ? {
                        ...s,
                        messageCount: s.messageCount + 1,
                        timestamp: new Date().toISOString(),
                      }
                    : s
                )
              )
            }

            // Update metadata if available
            if (type === 'warranties' && intermediateMetadata) {
              setMetadata(
                intermediateMetadata as Record<
                  string,
                  string | number | boolean | null
                >
              )
            }
          },
          abortSignal: abortController.signal,
        })

      const wasNewSession = pollingTargetSession === 'new-session'

      // Verifica se deve atualizar UI (usa ref para valor atual, não closure)
      const isActivePolling = activePollingSession.current === sessionId
      const currentViewingSession = currentSessionIdRef.current
      const isViewingThisSession =
        currentViewingSession === sessionId ||
        (wasNewSession && currentViewingSession === null)
      const shouldUpdateUI = isActivePolling && isViewingThisSession

      // ✅ CRITICAL: Set sessionId AFTER polling completes (for new sessions)
      // This prevents re-render during polling that would clear loading states.
      // Agora via URL: navigateToSession atualiza a query string, o effect
      // sincroniza a ref automaticamente.
      if (wasNewSession && shouldUpdateUI) {
        // Atualiza ref síncrono imediatamente para evitar que o effect
        // disparador dispare `loadSession` de novo quando a URL mudar.
        currentSessionIdRef.current = sessionId
        navigateToSession(sessionId)
        setCurrentSessionTitle(title || generateAutoTitle(text))
      }

      // ✅ CRITICAL: Update session in list BEFORE reloading from server
      // This prevents race condition where server reload overwrites local state
      // eslint-disable-next-line no-console
      console.log('[sendMessage] Agent response received, updating session', {
        sessionId,
        wasNewSession,
      })
      setSessions(prev => {
        const updated = prev.map(s => {
          if (s.id === sessionId) {
            const newCount = wasNewSession ? 2 : s.messageCount + 1
            // eslint-disable-next-line no-console
            console.log('[sendMessage] Incrementing for agent response', {
              id: s.id,
              oldCount: s.messageCount,
              newCount,
              wasNewSession,
            })
            return {
              ...s,
              messageCount: newCount,
              title: title || s.title,
              timestamp: new Date().toISOString(),
              status: s.status || 'in_progress',
            }
          }
          return s
        })
        return updated
      })

      // Only update chat UI if user is still viewing this session
      if (shouldUpdateUI) {
        const aiMsg: ChatMessageData = {
          id: `ai-${Date.now()}`,
          type: 'agent',
          content: response,
          timestamp: new Date().toISOString(),
        }
        setMessages(prev => [...prev, aiMsg])

        // Update metadata from poll response (already flat structure)
        if (type === 'warranties' && agentMetadata) {
          setMetadata(agentMetadata)

          // Load schema if not already loaded (needed for new sessions)
          if (!metadataSchema || metadataSchema.length === 0) {
            try {
              const historyData = await chatApi.getHistory(sessionId)
              if (historyData.metadataSchema) {
                setMetadataSchema(historyData.metadataSchema.fields || [])
              }
            } catch {
              // Silent fail - schema will be loaded on next interaction
            }
          }
        }
      }

      // Clear active polling tracking and saved startTime since job completed
      if (isActivePolling) {
        activePollingSession.current = null
        deleteSessionStartTime(sessionId)
      }

      // ✅ OPTIONAL: Reload sessions from server (commented to avoid race condition)
      // The optimistic update above is sufficient for immediate UI feedback
      // Uncomment if you need to sync other session data from server
      // await loadSessionsData()
    } catch (error) {
      // Check if error is due to abort
      if (error instanceof Error && error.message === 'Polling cancelled') {
        // Polling was cancelled - this is expected when navigating away
        return
      }
      // Trocar de conversa aborta o controller, mas o poll que já estava em
      // andamento ainda volta: sem esta checagem a bolha cairia na conversa
      // para onde a pessoa foi.
      if (abortController.signal.aborted) return
      // A mensagem da pessoa ficava na tela sem resposta nenhuma: o erro só
      // parava o "processando". Agora a falha vira uma bolha que diz o motivo.
      if (
        currentSessionIdRef.current === currentSessionId ||
        pollingTargetSession === 'new-session'
      ) {
        const errorMsg: ChatMessageData = {
          id: `error-${Date.now()}`,
          type: 'agent',
          content: `❌ ${describeApiError(error, 'Não consegui processar sua mensagem. Tente de novo.')}`,
          timestamp: new Date().toISOString(),
        }
        setMessages(prev => [...prev, errorMsg])
      }
    } finally {
      // Remove session from loading set
      setLoadingSessions(prev => {
        const next = new Set(prev)
        next.delete(sessionKey)
        return next
      })
      // Clear processing state when loading completes
      setProcessingStatus(PROCESSING_STATUS.IDLE)
      setProcessingStartTime(null)
    }
  }

  const handleCopyMessage = (content: string) => {
    navigator.clipboard.writeText(content)
  }

  // URL é a single source of truth: quando `currentSessionId` (derivado de
  // searchParams) mudar, carregamos a sessão. Cobre cold mount (deeplink
  // direto), clicks no sidebar (via navigateToSession), voltar/avançar do
  // browser, refresh — qualquer fonte de mudança de URL converge aqui.
  //
  // lastLoadedRef evita re-carregar a mesma sessão em renders subsequentes.
  // Aguardamos `sessions.length > 0` para que `loadSession` consiga preencher
  // o título; a sessão em si é carregada do backend mesmo quando ausente
  // da lista (ex: criada pelo scheduler num batch novo).
  const lastLoadedSessionRef = useRef<string | null>(null)
  useEffect(() => {
    if (!currentSessionId) {
      lastLoadedSessionRef.current = null
      return
    }
    if (sessions.length === 0) return
    if (lastLoadedSessionRef.current === currentSessionId) return
    lastLoadedSessionRef.current = currentSessionId
    loadSession(currentSessionId)
  }, [currentSessionId, sessions, loadSession])

  if (!hasPermission) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Icon name="bolt" size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Acesso Negado
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Você não tem permissão para usar este agente.
          </p>
        </div>
      </div>
    )
  }

  // Show loading state
  if (agentsLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="xl" className="text-brand-500" />
      </div>
    )
  }

  // Show error state if agent not found
  if (!agent) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Icon name="bolt" size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Agente não encontrado
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            O agente solicitado não existe ou você não tem permissão para
            acessá-lo.
          </p>
          <Link
            to="/agents"
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors"
          >
            Voltar para Agentes
          </Link>
        </div>
      </div>
    )
  }

  // Only show fullscreen loading when switching sessions AND no messages yet
  // If we have messages, always show them + inline loading indicator
  const shouldShowLoadingState =
    isLoadingDifferentSession && messages.length === 0

  return (
    <div
      data-testid="chat-layout-root"
      className="flex flex-col flex-1 min-h-0 w-full max-w-full overflow-hidden"
    >
      <div className="flex flex-col lg:flex-row flex-1 min-h-0 lg:gap-0 overflow-hidden">
        {/* Área principal do chat */}
        <div
          data-testid="chat-main-area"
          className="flex-1 grid grid-rows-[1fr_auto] min-h-[calc(100vh-77px)] overflow-hidden"
        >
          {/* Área de mensagens - com scroll independente */}
          <div
            data-testid="chat-messages-area"
            className="overflow-y-auto px-4 lg:px-6 py-4 lg:py-6 pb-32 lg:pb-6 min-h-0"
          >
            {/* Show fullscreen loading when switching sessions */}
            {shouldShowLoadingState ? (
              <ProcessingIndicator
                status={
                  processingStatus !== PROCESSING_STATUS.IDLE
                    ? processingStatus
                    : PROCESSING_STATUS.SENDING
                }
                startTime={processingStartTime ?? undefined}
                variant="fullscreen"
                onCancel={handleCancelProcessing}
              />
            ) : messages.length === 0 && !isCurrentSessionLoading ? (
              /* Empty state welcome when no messages and not loading */
              <EmptyStateWelcome
                agent={agent}
                metadata={metadata}
                onSnippetSelect={handleSnippetSelect}
              />
            ) : (
              /* Normal message list */
              <div className="space-y-6 max-w-6xl mx-auto">
                {messages.map(message => {
                  // Use sender name from message if available, otherwise fall back to current user
                  const messageUserName =
                    message.type === 'user' && message.sender?.name
                      ? message.sender.name
                      : user?.first_name || user?.username

                  return (
                    <ChatMessage
                      key={message.id}
                      message={message}
                      userName={messageUserName}
                      onCopy={handleCopyMessage}
                    />
                  )
                })}

                {/* Processing indicator - shown when waiting for response */}
                {isCurrentSessionLoading && (
                  <ProcessingIndicator
                    status={
                      processingStatus !== PROCESSING_STATUS.IDLE
                        ? processingStatus
                        : PROCESSING_STATUS.SENDING
                    }
                    startTime={processingStartTime ?? undefined}
                    variant="inline"
                    onCancel={handleCancelProcessing}
                  />
                )}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Mobile floating action buttons — versão compacta e menos invasiva.
              Posicionados acima do input, com tokens neutros para não competir
              com o conteúdo das mensagens. */}
          <div className="lg:hidden fixed bottom-24 right-3 z-40 flex flex-col gap-2">
            {type === 'warranties' && (
              <button
                onClick={() => {
                  setSidebarMode('metadata')
                  setIsHistorySidebarOpen(true)
                }}
                className="p-2.5 bg-surface-elevated hover:bg-gray-50 dark:hover:bg-gray-800 text-content-secondary border border-gray-200 dark:border-gray-700 rounded-full shadow-md transition-colors active:scale-95"
                aria-label="Ver Metadados"
                title="Ver Metadados"
              >
                <Icon name="file-text" size={20} />
              </button>
            )}

            <button
              onClick={() => {
                setSidebarMode('history')
                setIsHistorySidebarOpen(true)
              }}
              className="p-2.5 bg-surface-elevated hover:bg-gray-50 dark:hover:bg-gray-800 text-content-secondary border border-gray-200 dark:border-gray-700 rounded-full shadow-md transition-colors active:scale-95"
              aria-label="Ver Histórico"
              title="Ver Histórico"
            >
              <Icon name="clock" size={20} />
            </button>
          </div>

          <ChatInput
            value={inputValue}
            onChange={setInputValue}
            onSend={sendMessage}
            disabled={isCurrentSessionLoading || !canSendMessages}
            isMobile={false}
            onSnippetsClick={handleSnippetsClick}
            agentCode={type}
            readOnly={!canSendMessages}
          />
        </div>

        {/* Right sidebar toggle button (visible when collapsed) */}
        <button
          onClick={toggleRightSidebar}
          className="hidden lg:flex items-center justify-center w-6 h-12 my-auto flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-l-md transition-colors"
          aria-label={
            isRightSidebarExpanded ? 'Recolher painel' : 'Expandir painel'
          }
          title={isRightSidebarExpanded ? 'Recolher painel' : 'Expandir painel'}
        >
          <Icon
            name={isRightSidebarExpanded ? 'chevron-right' : 'chevron-left'}
            size={14}
          />
        </button>

        {/* Unified Sidebar - History or Metadata */}
        <div
          className={`${isRightSidebarExpanded ? 'lg:w-64 xl:w-80' : 'lg:w-0'} border-l border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hidden lg:flex flex-col min-h-0 flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden`}
        >
          {sidebarMode === 'metadata' && type === 'warranties' ? (
            <MetadataSidebar
              metadata={metadata}
              onUpdate={updateMetadata}
              schema={metadataSchema || []}
              status={sessions.find(s => s.id === currentSessionId)?.status}
              onStatusChange={status =>
                currentSessionId
                  ? updateSessionStatus(currentSessionId, status)
                  : Promise.resolve()
              }
              title={currentSessionTitle}
              onTitleChange={newTitle =>
                currentSessionId
                  ? updateSessionTitle(currentSessionId, newTitle)
                  : Promise.resolve()
              }
              onToggleMode={() => setSidebarMode('history')}
              toggleButtonText="Ver Histórico"
            />
          ) : (
            <SessionHistorySidebar
              sessions={sessions}
              groupedSessions={groupedSessions}
              currentSessionId={currentSessionId}
              hasMessages={messages.length > 0}
              onNewChat={startNewChat}
              onSelectSession={navigateToSession}
              onShareSession={
                chatSharingEnabled ? handleShareSession : undefined
              }
              formatTime={formatTime}
              onToggleMode={
                type === 'warranties'
                  ? () => setSidebarMode('metadata')
                  : undefined
              }
              toggleButtonText={
                type === 'warranties' ? 'Ver Metadados' : undefined
              }
              isMobile={false}
              onClose={() => setIsHistorySidebarOpen(true)}
            />
          )}
        </div>
      </div>

      {/* Mobile sidebar overlay - Metadata */}
      {sidebarMode === 'metadata' &&
        type === 'warranties' &&
        isHistorySidebarOpen && (
          <div
            data-testid="mobile-metadata-sidebar"
            className="lg:hidden fixed inset-0 z-50"
          >
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsHistorySidebarOpen(false)}
            />
            <div className="absolute right-0 top-0 bottom-0 w-80 bg-white dark:bg-gray-900 shadow-2xl border-l border-gray-200/50 dark:border-gray-700/50 flex flex-col">
              <MetadataSidebar
                metadata={metadata}
                onUpdate={updateMetadata}
                schema={metadataSchema || []}
                status={sessions.find(s => s.id === currentSessionId)?.status}
                onStatusChange={status =>
                  currentSessionId
                    ? updateSessionStatus(currentSessionId, status)
                    : Promise.resolve()
                }
                title={currentSessionTitle}
                onTitleChange={newTitle =>
                  currentSessionId
                    ? updateSessionTitle(currentSessionId, newTitle)
                    : Promise.resolve()
                }
                onToggleMode={() => setSidebarMode('history')}
                toggleButtonText="Ver Histórico"
                isMobile={true}
                onClose={() => setIsHistorySidebarOpen(false)}
              />
            </div>
          </div>
        )}

      {/* Mobile sidebar overlay - History */}
      {sidebarMode === 'history' && (
        <SessionHistorySidebar
          sessions={sessions}
          groupedSessions={groupedSessions}
          currentSessionId={currentSessionId}
          hasMessages={messages.length > 0}
          onNewChat={startNewChat}
          onSelectSession={loadSession}
          onShareSession={chatSharingEnabled ? handleShareSession : undefined}
          formatTime={formatTime}
          onToggleMode={
            type === 'warranties' ? () => setSidebarMode('metadata') : undefined
          }
          toggleButtonText={type === 'warranties' ? 'Ver Metadados' : undefined}
          isMobile={true}
          isOpen={isHistorySidebarOpen}
          onClose={() => setIsHistorySidebarOpen(false)}
        />
      )}

      <ChatInput
        value={inputValue}
        onChange={setInputValue}
        onSend={sendMessage}
        disabled={isCurrentSessionLoading || !canSendMessages}
        isMobile={true}
        onSnippetsClick={handleSnippetsClick}
        agentCode={type}
        readOnly={!canSendMessages}
      />

      {/* Snippet Components */}
      <SnippetSelector
        agentCode={type || ''}
        isOpen={showSnippets}
        onClose={() => setShowSnippets(false)}
        onSnippetSelect={handleSnippetSelect}
      />

      <SnippetFormModal
        snippet={selectedSnippet}
        isOpen={showSnippetForm}
        onClose={() => {
          setShowSnippetForm(false)
          setSelectedSnippet(null)
        }}
        onSubmit={handleSnippetFormSubmit}
        metadata={metadata}
      />

      {/* Share Session Modal */}
      {chatSharingEnabled && (
        <ShareSessionModal
          isOpen={shareModalSession !== null}
          onClose={() => setShareModalSession(null)}
          session={shareModalSession}
          onSharesUpdated={handleSharesUpdated}
        />
      )}
    </div>
  )
}
